/**
 * State-based achievements: evaluate the save and award anything satisfied.
 * Callers run it at natural checkpoints (episode screen, shop purchase, bag
 * open, campaign win, inbox delivery) — award() is idempotent, so sweeping
 * often is free. Returns what was NEWLY earned so callers can toast.
 * Spec: docs/superpowers/specs/2026-08-26-achievements-batch2-phantom-design.md
 */
import type { StorageLike } from '../account/saveSync';
import type { Achievement, AchievementId } from '../config/achievements';
import { SHOP_SALE_TOWERS, availableTowers } from '../config/unlocks';
import { LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS } from '../config/levels';
import { HUB_CHAPTERS, isHubLevelUnlocked } from '../config/hubLevels';
import { DARK_SHOP_TOWERS, LIGHT_SHOP_TOWERS } from '../config/originShop';
import { originEventLive } from '../config/originEvent';
import { readSettings } from '../config/gameSettings';
import { QUESTER_STREAK, QUESTS_KEY, parseQuests } from '../config/quests';
import { ProgressService } from './progressService';
import { RankService } from './rankService';

/** Mushy Mayhem and The Shroombellion — pinned by achievementSweep.test. */
const MUSHY_MAYHEM_ID = 115;
const SHROOMBELLION_ID = 121;

export function sweepAchievements(storage: StorageLike): Achievement[] {
  const progress = new ProgressService(storage, LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
  const rank = new RankService(storage);
  const towers = new Set<string>(progress.unlockedExtras());
  const roster = availableTowers(progress.enteredLevels(), progress.unlockedExtras());
  const coins = progress.coins();

  const satisfied: AchievementId[] = [];
  if (towers.has('wise')) satisfied.push('bewise');
  if (towers.has('firemage')) satisfied.push('letitburn');
  if (towers.has('aquasorcerer')) satisfied.push('aquatic');
  if (progress.evolvedTowers().length > 0) satisfied.push('evolution');
  if (progress.equippedTowers(roster).length >= ProgressService.MAX_EQUIPPED) {
    satisfied.push('loadout');
  }
  if (SHOP_SALE_TOWERS.every((t) => towers.has(t))) satisfied.push('shoplifter');
  if (coins >= 500) satisfied.push('richness');
  if (coins >= 1500) satisfied.push('mansion');

  // ── Diamond (spec 2026-10-29). Every one of these is a fact of the save,
  // so a player who did it before the update earns it on the next sweep.
  // The Stink: the SAME test the Hub's chapter card uses to stop reading
  // LOCKED, so the badge and the card can never disagree.
  const fungi = HUB_CHAPTERS.find((c) => c.number === 2);
  if (fungi && isHubLevelUnlocked(fungi.levels[0].id, progress.hubCompleted())) {
    satisfied.push('thestink');
  }
  if ((parseQuests(storage.getItem(QUESTS_KEY)).streak?.count ?? 0) >= QUESTER_STREAK) {
    satisfied.push('quester');
  }
  // Normal track only: Peaceful is the track where nothing can hurt you.
  if (progress.hubStars(MUSHY_MAYHEM_ID) > 0) satisfied.push('mushmillenium');
  if (progress.hubStars(SHROOMBELLION_ID) > 0) satisfied.push('shroombellionfallen');
  if (towers.has('goldenshroom')) satisfied.push('friendlyfungi');
  // Limited time: only while the Origin Event runs; kept forever once earned.
  if (originEventLive()) {
    if (DARK_SHOP_TOWERS.every((t) => towers.has(t))) satisfied.push('ghosting');
    if (LIGHT_SHOP_TOWERS.every((t) => towers.has(t))) satisfied.push('halohunting');
  }
  // Gearbox: a Settings toggle is on (flipping one awards it at once too).
  const settings = readSettings();
  if (Object.values(settings).some((v) => v === true)) satisfied.push('gearbox');

  const newly: Achievement[] = [];
  for (const id of satisfied) {
    const a = rank.award(id);
    if (a) newly.push(a);
  }
  return newly;
}
